import { MatchType, SearchType } from 'utils/enum';
import { AdvancedSearchParams } from 'utils/models';

type CollectionRef = { id: string; name: string };

// Languages the FilterBar offers. `value` must equal the index's bare
// media_language code ("en", not "en-AU"): the lang: filter is an exact terms
// match, and several lang: tokens OR together.
export const SEMANTIC_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

export const semanticLanguageLabel = (code: string): string =>
  SEMANTIC_LANGUAGES.find(({ value }) => value === code)?.label ?? code;

/** Collection ids a semantic search is scoped to; empty means all. Takes the
 * open collection by id, so it works before the collection list has loaded
 * (a page reload on /collections/<id>). */
export const resolveSemanticCollections = (
  openCollectionId: string | null,
  picked: CollectionRef[],
  tagPool: CollectionRef[],
  isTagActive: boolean,
): string[] => {
  const pool = (isTagActive && !picked.length ? tagPool : picked).map(
    ({ id }) => id,
  );
  if (!openCollectionId) return pool;

  const isInPool = pool.includes(openCollectionId);
  if (!pool.length || isInPool || !isTagActive) return [openCollectionId];
  return pool;
};

const quote = (value: string): string => `"${value.replace(/"/g, "'")}"`;

/** The search text only. Filters go in their own request fields
 * (`buildSemanticSearchFilters`), so text can never be read as filter syntax.
 * A title search matches the text as typed, so it is never quoted. */
export const buildSemanticSearchQuery = (
  searchTerm: string,
  filters: AdvancedSearchParams,
): string => {
  const text = (searchTerm || '').trim();
  const isExact =
    filters.matchType === MatchType.EXACT_PHRASE &&
    filters.searchBy !== SearchType.TITLE;
  return text && isExact ? quote(text) : text;
};

/** `filters` must already be narrowed to semantic's options
 * (`pickSupportedFilters(params, true)`). */
export const buildSemanticSearchFilters = (filters: AdvancedSearchParams) => ({
  type: filters.type ?? null,
  speakers: filters.speakerNames,
  languages: filters.languages,
  excludeWords: filters.excludeWords,
});
